import { createContext, useContext, type ReactNode } from 'react'
import toast, { Toaster } from 'react-hot-toast'

type ToastType = 'success' | 'error' | 'info' | 'warning'

interface ToastContextValue {
  showToast: (message: string, type?: ToastType) => void
  success: (message: string) => void
  error: (message: string) => void
  info: (message: string) => void
  warning: (message: string) => void
  loading: (message: string) => string
  dismiss: (toastId?: string) => void
  promise: <T>(
    promise: Promise<T>,
    msgs: {
      loading: string
      success: string
      error: string | ((err: Error) => string)
    }
  ) => Promise<T>
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined)

interface ToastProviderProps {
  children: ReactNode
}

export function ToastProvider({ children }: ToastProviderProps) {
  const showToast = (message: string, type: ToastType = 'info') => {
    switch (type) {
      case 'success':
        toast.success(message)
        break
      case 'error':
        toast.error(message)
        break
      case 'warning':
        toast(message, { icon: '⚠️' })
        break
      default:
        toast(message, { icon: 'ℹ️' })
    }
  }

  const value: ToastContextValue = {
    showToast,
    success: (message) => toast.success(message),
    error: (message) => toast.error(message),
    info: (message) => toast(message, { icon: 'ℹ️' }),
    warning: (message) => toast(message, { icon: '⚠️' }),
    loading: (message) => toast.loading(message),
    dismiss: (toastId) => toast.dismiss(toastId),
    promise: (promise, msgs) => toast.promise(promise, msgs),
  }

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            maxWidth: 'min(90vw, 420px)',
            wordBreak: 'break-word',
            overflowWrap: 'anywhere',
          },
        }}
      />
    </ToastContext.Provider>
  )
}

export function useToast() {
  const context = useContext(ToastContext)
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}
